<template>

    <div class="participant-import mb-3 mt-page">

        <!-- action buttons -->
        <div class="float-right">
            <b-btn variant="default" class="mr-2"
                   @click="reset()">
                {{ $i18n.gt('LABEL_RESET') }}
            </b-btn>
            <b-btn variant="primary"
                   :disabled="!dataRows"
                   @click="upload()">
                {{ $i18n.gt('LABEL_IMPORT') }}
            </b-btn>
        </div>

        <h1>{{ $i18n.gt('HEADING_IMPORT_PATIENTS') }}</h1>

        <b-row class="justify-content-center">
            <b-col cols="6" col-offset="3">
                <b-form-file
                        v-model="file"
                        accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                        :state="Boolean(file)"
                        :placeholder="$i18n.gt('LABEL_SELECT_FILE')"
                        drop-placeholder="Drop file here...">
                </b-form-file>

                <div class="my-3"
                     v-if="file">
                    <b-form-checkbox v-model="hasHeaderRow">
                        {{ $i18n.gt('LABEL_FILE_HEADER_ROW') }}
                    </b-form-checkbox>
                </div>

                <div class="my-3"
                     v-if="file">
                    <label>{{ $i18n.gt('LABEL_ATTACH_PARTICIPANTS') }}:</label>
                    <b-form-select v-model="planId" :options="planOptions">
                    </b-form-select>
                </div>
                <div class="my-3"
                     v-if="file">
                    <label>{{ $i18n.gt('LABEL_ENTER_COMMENT') }}:</label>
                    <b-form-input type="text" v-model="comment">
                    </b-form-input>
                </div>
            </b-col>
        </b-row>

        <b-row class="justify-content-center">
            <b-col cols="6" col-offset="3">
                <b-alert show variant="info" class="mt-3">
                    <div v-if="!dataRows">
                        <p>
                            {{ $i18n.gt('LABEL_SELECT_CSV') }}
                        </p>
                        <p>
                            {{ $i18n.gt('LABEL_CSV_DATA') }}:
                            <br/>
                            <b-btn variant="primary" size="sm" class="mt-2"
                                   href="/csv-templates/Wellcoaches participant import template.csv">
                                <shs-icon name="data-transfer-download"></shs-icon>
                                {{ $i18n.gt('LABEL_DOWNLOAD_CSV_TEMPLATE') }}
                            </b-btn>
                        </p>
                    </div>
                    <p v-else>
                        {{ $i18n.gt('LABEL_REVIEW_DATA_ERRORS') }}
                    </p>
                </b-alert>
            </b-col>
        </b-row>
        <b-row class="justify-content-center">
            <b-col cols="6" col-offset="3">
                <b-alert :show="hasInvalidData" variant="danger">
                    {{ $i18n.gt('LABEL_INVALID_DATA') }}
                </b-alert>
            </b-col>
        </b-row>

        <b-row class="justify-content-center">
            <b-col>
                <spinner-until :condition="!loading">
                    <table class="table table-striped my-3">
                        <thead>
                        <tr>
                            <th class="text-center"
                                :colspan="permittedColumns.length">
                                <h3>{{ $i18n.gt('HEADING_REQUIRED_COLUMNS') }}</h3>
                            </th>
                        </tr>
                        <tr>
                            <th v-for="(col, c) in permittedColumns" :key="c">
                                {{col[1]}}
                            </th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="(row,r) in dataRows" :key="r">
                            <td v-for="(col, c) in permittedColumns" :key="c"
                                :class="getDataCellClass(r,c)">
                                {{ row[c] }}
                            </td>
                        </tr>
                        </tbody>
                    </table>
                </spinner-until>
            </b-col>
        </b-row>

    </div>
</template>

<script>

    import Vue from 'vue'
    import Papa from 'papaparse'
    import moment from 'moment'
    import * as _ from 'lodash'

    /**
     * Page to import participants from a CSV file
     */
    export default Vue.extend({
        name: 'ParticipantImport',
        data () {
            return {
                file: null,
                hasHeaderRow: true,
                loading: false,
                comment: 'Batch import ' + moment().format('M/D/YY'),
                dataRows: null,
                permittedColumns: [
                    ['external_id', 'Insurance ID'],
                    ['email', 'Email'],
                    ['firstname', 'Firstname'],
                    ['lastname', 'Lastname'],
                    ['gender', 'Gender'],
                    ['pronouns', 'Pronouns'],
                    ['ethnicity', 'Ethnicity'],
                    ['dob', 'Date of Birth'],
                    ['phone_primary', 'Primary Phone'],
                    ['phone_secondary', 'Secondary Phone'],
                    ['address', 'Address'],
                    ['address2', 'Address 2'],
                    ['city', 'City'],
                    ['state', 'State'],
                    ['zip', 'Zip'],
                ],
                requiredColumns: [
                    'email',
                    'firstname',
                    'lastname',
                    'gender',
                    'ethnicity',  // NATIVE/ASIAN/AFRICAN/HISPANIC/PACIFIC/WHITE
                    'dob',
                ],
                planId: null,
                planOptions: [],
            }
        },
        computed: {
            hasInvalidData () {
                let has = false
                _.each(this.dataRows, (row, r) => {
                    _.each(row, (col, c) => {
                        if (this.getDataCellClass(r, c) == 'error-cell') {
                            has = true
                        }
                    })
                })
                return has
            }
        },
        methods: {
            parseFile () {
                this.loading = true
                let reader = new FileReader()
                reader.readAsText(this.file, 'UTF-8')
                reader.onload = (evt) => {
                    Papa.parse(evt.target.result, {
                        worker: true,
                        complete: (results, file) => {
                            this.loading = false
                            this.dataRows = results.data
                        },
                        error: () => {
                            this.loading = false
                            alert('Error loading CSV file')
                        }
                    })
                }
                reader.onerror = function () {
                    this.loading = false
                    alert('Error loading CSV file')
                }
            },
            getDataCellClass (row, col) {
                const colName = this.permittedColumns[col][0]
                const value = this.dataRows[row][col]
                if (this.hasHeaderRow && row == 0) {
                    // header row is only valid if hasHeaderRow is checked
                    return 'header-cell'
                } else if (colName == 'gender' && value != 'M' && value != 'F') {
                    return 'error-cell'
                } else if (colName == 'ethnicity' && ['NATIVE', 'ASIAN', 'AFRICAN', 'HISPANIC', 'PACIFIC', 'WHITE'].indexOf(value) == -1) {
                    return 'error-cell'
                } else if (colName == 'dob' && !moment(value).isValid()) {
                    return 'error-cell'
                } else if (!value && this.requiredColumns.indexOf(colName) != -1) {
                    return 'missing-required-cell'
                } else {
                    return ''
                }
            },
            upload () {
                this.$api.importParticipants(this, this.dataRows, this.hasHeaderRow, this.comment, this.planId, this.$user.practitionerPracticeId())
                    .then((response) => {
                        this.$toasted.show('Participants imported', { duration: 3000 })
                    })
                    .catch((e) => {
                        this.$utils.handleError(e, 'Error importing participants')
                    })
            },
            reset () {
                this.dataRows = null
                this.file = null
            }
        },
        mounted() {
            // load plans for current practice, for input
            const filters = { practice_id: { field: 'practice_id', value: this.$user.practitionerPracticeId() } }
            const expands = ['plan']
            this.$api.getItems(null, 'plan-practice', filters, {}, expands)
                .then((items) => {
                    this.planOptions = _.map(items, (pp) => {
                        return {
                            value: pp.plan.id, text: pp.plan.name
                        }
                    })
                })
        },
        watch: {
            // watch for file selection
            file (newVal, oldVal) {
                if (newVal) {
                    this.parseFile()
                }
            }
        }
    })

</script>

<style lang="scss">
    .participant-import {
        .header-cell {
            color: #ccc;
        }

        .error-cell {
            color: red;
        }
    }
</style>
